import { useState, useEffect, useRef, useCallback } from 'react';
import { toast } from 'sonner';
import { qryCopilotValueApi, getCopilotValueByTaskIdApi } from '@/api/copilot';
import { EXTERNAL_USER_ID } from '@/config/env';
import { usePollingApi } from '@/hooks/usePollingApi';
import { useAuth } from '@/contexts/AuthContext';
import useTrendData from '@/stores/trendStore';
import useUserStore from '@/stores/userStore';
import useCopilotValueStore from '@/stores/copilotValueStore';
import { CopilotValue1, CopilotValue2, CopilotValue3 } from '@/stores/types/copilotValue';

interface UseCopilotDataReturn {
  loading: {
    userProfile: boolean;
    extendedProfile: boolean;
    portfolioDiagnosis: boolean;
    all: boolean;
  };
  fetchCopilotData: () => Promise<void>;
  refresh: () => void;
  isRefreshing: boolean;
}

/**
 * 管理 Copilot 数据获取和轮询
 */
export function useCopilotData(
  customerOverviewRef: React.RefObject<{ init: () => void }>
): UseCopilotDataReturn {
  const { externalUserId, followUserId } = useAuth();
  const { updateTrendData, trendTimer, setTrendTimer } = useTrendData();
  const { updateCopilotValue1, updateCopilotValue2, updateCopilotValue3 } = useCopilotValueStore();
  const { updateTaskId, updateUserInfo } = useUserStore();
  const [loading1, setLoading1] = useState(true);
  const [loading2, setLoading2] = useState(true);
  const [loading3, setLoading3] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const hasFetched = useRef(false);

  // 创建三个轮询实例
  const polling1 = usePollingApi(
    ({ taskId: tId }) => {
      return getCopilotValueByTaskIdApi({
        externalUserId: externalUserId || EXTERNAL_USER_ID,
        reqType: 1,
        taskId: tId,
        followUserId
      });
    },
    response => response?.status === 'false',
    {
      interval: 1000,
      onSuccess: (data: CopilotValue1) => {
        updateCopilotValue1(data);
        const userInfoData = {
          user_tags: (data.user_tags || '').split('、'),
          profile_summary: data.profile_summary
        };
        updateUserInfo(userInfoData);
        setLoading1(false);
      },
      onError: error => {
        console.error('轮询失败 (reqType: 1):', error);
        updateCopilotValue1({
          investment_behavior_analysis: '',
          position_analysis: '',
          profile_summary: '',
          user_tags: ''
        });
        setLoading1(false);
      }
    }
  );

  const polling2 = usePollingApi(
    ({ taskId: tId }) => {
      return getCopilotValueByTaskIdApi({
        externalUserId: externalUserId || EXTERNAL_USER_ID,
        reqType: 2,
        taskId: tId,
        followUserId
      });
    },
    response => response?.status === 'false',
    {
      interval: 1000,
      onSuccess: (data: CopilotValue2) => {
        updateCopilotValue2(data);
        if (Array.isArray(data.customerInfo) && data.customerInfo.length > 0) {
          const customerInfo = data.customerInfo[0];
          updateUserInfo({
            customer_name: customerInfo.customer_name,
            risk_level: customerInfo.risk_level,
            investment_experience: customerInfo.investment_experience,
            total_assets: customerInfo.total_assets
          });
        }
        setLoading2(false);
      },
      onError: error => {
        console.error('轮询失败 (reqType: 2):', error);
        updateCopilotValue2({
          dataType: '',
          customerInfo: []
        });
        setLoading2(false);
      }
    }
  );

  const polling3 = usePollingApi(
    ({ taskId: tId }) => {
      return getCopilotValueByTaskIdApi({
        externalUserId: externalUserId || EXTERNAL_USER_ID,
        reqType: 3,
        taskId: tId,
        followUserId
      });
    },
    response => response?.status === 'false',
    {
      interval: 1000,
      onSuccess: (data: CopilotValue3) => {
        updateCopilotValue3(data);
        setLoading3(false);
      },
      onError: error => {
        console.error('轮询失败 (reqType: 3):', error);
        updateCopilotValue3({});
        setLoading3(false);
      }
    }
  );

  // 获取 Copilot 初始数据
  const fetchCopilotData = async () => {
    try {
      const response = await qryCopilotValueApi({
        externalUserId: externalUserId || EXTERNAL_USER_ID,
        reqType: 0,
        followUserId
      });

      if (response) {
        customerOverviewRef.current?.init();
        updateTaskId(response);
        const params = {
          externalUserId: externalUserId || EXTERNAL_USER_ID,
          taskId: response,
          followUserId
        };

        // 启动三个轮询
        polling1.startPolling({ ...params, reqType: 1 });
        polling2.startPolling({ ...params, reqType: 2 });
        polling3.startPolling({ ...params, reqType: 3 });
      }
    } catch (error) {
      console.error('获取Copilot数据失败:', error);
      setLoading1(false);
      setLoading2(false);
      setLoading3(false);
    }
  };

  const taskId = useUserStore(state => state.taskId);
  // 获取趋势数据
  const fetchTrendData = useCallback(async () => {
    if (!taskId) return;

    if (trendTimer) {
      clearInterval(trendTimer);
    }

    const timer = setInterval(async () => {
      try {
        if (timer) {
          clearInterval(timer);
        }
        const response = await getCopilotValueByTaskIdApi({
          externalUserId: externalUserId || EXTERNAL_USER_ID,
          reqType: 16,
          taskId,
          followUserId
        });
        updateTrendData(response?.trend || []);
      } catch (error) {
        console.error('获取趋势数据失败:', error);
      }
    }, 3000);

    setTrendTimer(timer);
  }, [taskId]);

  // 当 taskId 变化时，获取趋势数据和市场分析
  useEffect(() => {
    if (taskId) {
      fetchTrendData();
    }
  }, [taskId]);

  // 组件挂载时获取数据
  useEffect(() => {
    if (!hasFetched.current) {
      hasFetched.current = true;
      fetchCopilotData();
    }
  }, []);

  // 刷新数据
  const refresh = useCallback(() => {
    if (loading1) {
      toast.error('客户画像数据获取中，请稍后再试');
      return;
    }
    if (loading2) {
      toast.error('客户画像额外信息数据获取中，请稍后再试');
      return;
    }
    if (loading3) {
      toast.error('持仓诊断数据获取中，请稍后再试');
      return;
    }

    setIsRefreshing(true);
    setLoading1(true);
    setLoading2(true);
    setLoading3(true);

    fetchCopilotData().finally(() => {
      setIsRefreshing(false);
    });
  }, [loading1, loading2, loading3, fetchCopilotData]);

  return {
    loading: {
      userProfile: loading1,
      extendedProfile: loading2,
      portfolioDiagnosis: loading3,
      all: loading1 || loading2 || loading3
    },
    fetchCopilotData,
    refresh,
    isRefreshing
  };
}
